/**
 * "Can we open yet?" (docs/ONBOARDING.md owner path, steps 1 and 4). One gate built from the sourced Utah roadmap
 * (setup.ts BUSINESS_SETUP) for the location's permit type, plus the food-safety must-haves the app can check from
 * its own records: the permit on file, a certified food safety manager on file where required, every worker's food
 * handler permit on track (onboarding.ts 14/30-day windows), and daily logs set up.
 *
 * Steps outside food safety (entity, tax, insurance, employment) come from the same research and show as
 * `kind: "ask_a_pro"` with the office that handles them. They never block the gate and are never presented as handled.
 * Nothing here is invented: every step and office is in docs/source/research/business-setup-utah.md or
 * food-truck-utah.md.
 */
import { onboardingStatus } from "./onboarding.ts";
import { STAGES, menuForStage } from "./playbooks.ts";
import { BUSINESS_SETUP, setupForPlace, type SetupStep } from "./setup.ts";
import type { Credential, Employee, Id, IsoDate, OrgKind, PermitType } from "./model.ts";
import { OPENING_UT } from "./states/ut/opening.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { wordsFor } from "./states/words.ts";
import { packFor, stateOrDefault } from "./states/index.ts";
import { SHAPES, authorityFor, homeOptions, rulesWorld, shapesAt, valueOf } from "./ruleshapes.ts";
import { PRE_OPENING_STEP_ID, inspectionPlanLine, isPreOpeningItemStepId, preOpeningChecklist, preOpeningItemStepId, type PreOpeningLine } from "./preopening.ts";

const WORDS: Record<UsState, StateWords<typeof OPENING_UT>> = perStateTable("OPENING", OPENING_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

export const NOT_LEGAL_ADVICE = "Informational only, not legal, tax or accounting advice. Rules differ by city and county: check with yours.";

/* ─────────────── step 1: what are you? ─────────────── */

export interface BusinessType {
  id: string;
  label: string;
  supported: boolean;
  orgKind?: OrgKind;
  permitType?: PermitType;
  /** Suggested operations-ladder stage (playbooks.ts) and starter menu (MENU_ITEMS ids); the owner edits both. */
  stage?: number;
  menuItemIds: string[];
  note?: string;
  link?: string;
}

const businessTypesForCache = new Map<UsState, BusinessType[]>();
/** Built once per state (callers look these up often). */
export function businessTypesFor(state?: UsState): BusinessType[] {
  const k = stateOrDefault(state);
  let v = businessTypesForCache.get(k);
  if (!v) { v = buildBusinessTypesFor(k); businessTypesForCache.set(k, v); }
  return v;
}

function buildBusinessTypesFor(state?: UsState): BusinessType[] {
  const w = words(state);
  return [
  // Audit #12: a restaurant or bar sat on ladder stage 3 ("Smash-burger food truck", truck permit only), so setup warned
  // about the wrong permit. Stage 4 (scratch kitchen) is a permitted kitchen; their starter menus are unchanged.
  { id: "restaurant", label: "Restaurant", supported: true, orgKind: "restaurant", permitType: "food_establishment", stage: 4, menuItemIds: ["smash-burger", "fries", "chili-soups"] },
  { id: "coffee_shop", label: "Coffee shop", supported: true, orgKind: "coffee_shop", permitType: "food_establishment", stage: 1, menuItemIds: menuForStage(1) },
  { id: "food_truck", label: "Food truck or cart", supported: true, orgKind: "restaurant", permitType: "mobile_food_business", stage: 3, menuItemIds: ["smash-burger", "fries"] },
  { id: "bakery_cafe", label: "Bakery or café", supported: true, orgKind: "coffee_shop", permitType: "food_establishment", stage: 2, menuItemIds: ["espresso-drinks", "milk-drinks", "bought-pastries", "house-bakes", "cream-pastries"] },
  { id: "catering", label: "Catering", supported: true, orgKind: "restaurant", permitType: "food_establishment", stage: 5, menuItemIds: ["chili-soups", "cut-salad", "buffet-line"] },
  { id: "bar_with_food", label: "Bar with food", supported: true, orgKind: "restaurant", permitType: "food_establishment", stage: 4, menuItemIds: ["smash-burger", "fries"] },
  { id: "community_kitchen", label: "Church or community kitchen", supported: true, orgKind: "community_space", permitType: "private_members_only", stage: 4, menuItemIds: ["chili-soups", "buffet-line", "cut-salad"], note: "Food served to the public at an event needs a county temporary food event permit." },
  { id: "event_booth", label: "Event booth", supported: true, orgKind: "community_space", permitType: "temporary_event", stage: 3, menuItemIds: ["smash-burger", "fries"] },
  // Concepts on a path (owner, 2026-09-28): equipment kits for these come from research/concept-kits.md.
  // Starter menus only where the sources name the food AND a sourced MENU_ITEMS flow covers it. Otherwise the menu
  // starts empty and setup asks the owner to pick (never the coffee ladder: docs/ux/STORIES-OPERATORS.md §0.3).
  // Ice cream and soft serve: the frozen-dessert dishes (playbooks.ts, menu-items.md MR13 to MR15) are their starter menus.
  { id: "ice_cream_shop", label: "Ice cream shop", supported: true, orgKind: "restaurant", permitType: "food_establishment", stage: 2, menuItemIds: ["scooped-ice-cream"] },
  { id: "soft_serve", label: "Soft serve spot", supported: true, orgKind: "restaurant", permitType: "food_establishment", stage: 2, menuItemIds: ["soft-serve"] },
  // concept-kits Kit 4: beans and meats cooled at the commissary and reheated to 165°F before hot holding (R102-12(2),
  // FC 3-403.11) = "tacos-rice-beans"; salsa fresca with cut tomatoes, cabbage or lettuce and cut fruit at 41°F = "cut-salad".
  { id: "taco_truck", label: "Taco truck", supported: true, orgKind: "restaurant", permitType: "mobile_food_business", stage: 3, menuItemIds: ["tacos-rice-beans", "cut-salad"] },
  // BBQ (concept-kits Kit 5: smoked brisket, pork, chicken) = "smoked-meats" (long hold, cooling large cuts, the special-process line).
  { id: "bbq_joint", label: "BBQ joint", supported: true, orgKind: "restaurant", permitType: "food_establishment", stage: 4, menuItemIds: ["smoked-meats"] },
  // MEHK: the statute sets limits (26B-7-416(8)), not a menu; home cooks sell anything from dinners to baked goods.
  // Only where the state's home shape is a permitted home kitchen (rule shapes: Utah yes; Nevada has none, NRS 580.600).
  ...(valueOf(SHAPES[stateOrDefault(state)].state.home.model) !== "mehk" ? [] : [<BusinessType>{ id: "mehk", label: "Home-based food business (MEHK)", supported: true, orgKind: "restaurant", permitType: "mehk" as PermitType, stage: 2, menuItemIds: [] as string[] }]),
  // Source: business-setup-utah.md A1 (UDAF: Cottage Food Production).
  ...(w.cottageFoodNote ? [{ id: "cottage_food", label: "Cottage food (shelf-stable food from home)", supported: false, menuItemIds: [], note: w.cottageFoodNote, ...(w.cottageFoodLink ? { link: w.cottageFoodLink } : {}) }] : []),
  ];
}

export const BUSINESS_TYPES: BusinessType[] = businessTypesFor("UT");

/**
 * The business types for a place, with its health authority's home rule (rule shapes): in Clark County a food business can't
 * run from a private home (SNHD 2-103(A)), so the cottage food choice says so. Utah's list is businessTypesFor("UT"), unchanged.
 */
export function businessTypesForPlace(state?: UsState, county?: string | null): BusinessType[] {
  const h = homeOptions(shapesAt({ state: stateOrDefault(state), county }));
  // A home kitchen where the place's own authority permits one but the state layer doesn't (a California county that opted in
  // to MEHKO, HSC 114367). Utah's state layer already lists it; Nevada's never permits it: neither changes.
  const base = businessTypesFor(state);
  const list = h.mehk && !base.some((b) => b.id === "mehk")
    ? [...base, <BusinessType>{ id: "mehk", label: "Home kitchen (MEHKO)", supported: true, orgKind: "restaurant", permitType: "mehk", stage: 2, menuItemIds: [], note: "Allowed here: your county or city permits home kitchens (HSC 114367)." }]
    : base;
  const barred = h.lines.find((l) => l.id === "barred");
  if (!barred) return list;
  return list.map((b) => (b.id === "cottage_food" ? { ...b, note: `${b.note ?? ""} ${barred.line} (${barred.citation})`.trim() } : b));
}

/**
 * The permit types a place can hold: the state's list, less a home kitchen permit where the place's own authority has no
 * home-kitchen program. California's MEHKO is a county or city opt-in (HSC 114367): in a county that didn't opt in (Fresno),
 * "Home kitchen permit" led to a permit path for a permit nobody there issues. With no county yet, the state's list stands
 * (the permit's own line says "where your county or city allows it"). Utah's and Nevada's lists are unchanged.
 */
export function permitsForPlace(state?: UsState, county?: string | null): PermitType[] {
  const all = [...packFor(state).permits];
  if (!county || !all.includes("mehk")) return all;
  return homeOptions(shapesAt({ state: stateOrDefault(state), county })).mehk ? all : all.filter((p) => p !== "mehk");
}

export const businessType = (id: string, state?: UsState) => businessTypesFor(state).find((b) => b.id === id);

/**
 * The menu a new place of this type starts with: the type's own sourced list, never the cumulative coffee ladder
 * (menuForStage). `pick: true` means the sources have no menu for this type, so setup asks the owner to choose.
 */
export function starterMenu(typeId: string | undefined): { menuItemIds: string[]; pick: boolean } {
  const ids = (typeId ? businessType(typeId)?.menuItemIds : undefined) ?? [];
  return { menuItemIds: [...ids], pick: ids.length === 0 };
}

/* ─────────────── setup questions that depend on the type (audit #12) ─────────────── */

/** "Is it faith-based?" is asked only of a church or community kitchen (a taproom was asked about Latter-day Saints). */
export const asksFaith = (typeId: string | undefined) => typeId === "community_kitchen";

/**
 * The ladder steps that fit this permit (a restaurant never sees "Smash-burger food truck"; a truck sees only the
 * truck). Each step stands alone (stageMenu), so "Scratch kitchen" no longer brings espresso and cold brew with it.
 */
export function stagesForPermit(permit: PermitType | undefined): typeof STAGES {
  return permit ? STAGES.filter((s) => s.permitOptions.includes(permit)) : STAGES;
}
/** One ladder step's own menu, not the steps below it added up (menuForStage). */
export function stageMenu(n: number): string[] {
  return [...(STAGES.find((s) => s.n === n)?.menuItemIds ?? [])];
}

/**
 * Suggested opening hours by business type (audit #12: everyone got 7 AM–3 PM). Not sourced: typical hours, shown as
 * "Suggested hours" for the owner to change. Types without a typical day (catering, events, church kitchens, home
 * businesses) get none, and setup keeps asking.
 */
export const SUGGESTED_HOURS: Record<string, { open: string; close: string }> = {
  coffee_shop: { open: "07:00", close: "15:00" },
  bakery_cafe: { open: "07:00", close: "15:00" },
  restaurant: { open: "11:00", close: "22:00" },
  bar_with_food: { open: "16:00", close: "01:00" },
  food_truck: { open: "11:00", close: "20:00" },
  taco_truck: { open: "11:00", close: "20:00" },
  bbq_joint: { open: "11:00", close: "21:00" },
  ice_cream_shop: { open: "12:00", close: "21:00" },
  soft_serve: { open: "12:00", close: "21:00" },
};
export const suggestedHours = (typeId: string | undefined) => (typeId ? SUGGESTED_HOURS[typeId] : undefined);

/* ─────────────── step 4: the gate ─────────────── */

export type OpeningStepKind = "food_safety" | "ask_a_pro";

export interface OpeningStep {
  id: string;
  title: string;
  why: string;
  kind: OpeningStepKind;
  done: boolean;
  /** Not done yet, but inside its legal window (food handler permits within 30 days). Doesn't block. */
  onTrack: boolean;
  /** Checked from records (permit number, credentials, people, tasks). Auto steps can't be ticked by hand. */
  auto: boolean;
  /** Blocks "Can we open yet?" until done or on track. `ask_a_pro` steps never block. */
  required: boolean;
  who: string; // the office to ask, or "You"
  link: string;
  citation: string;
  verifyLocal: boolean;
  deadline?: string;
  /** Plain words about what's missing ("Leo: permit due in 12 days"). */
  detail?: string;
  /** The office's sourced phone (opening guide). */
  phone?: string;
  /** What to bring, from the rule's own list (opening guide). */
  bring?: string[];
  /** "Get ready for your opening inspection": each item, whose it is, what proves it, and whether it's ticked (preopening.ts). */
  checklist?: (PreOpeningLine & { done: boolean; stepId: string })[];
  /** When the first inspection is likely and how often after (the authority's inspection.frequency), when known. */
  plan?: string;
}

export interface OpeningGate {
  ready: boolean;
  steps: OpeningStep[];
  blocking: { id: string; title: string; detail?: string }[];
  notLegalAdvice: string;
}

export interface OpeningGateInput {
  permitType: PermitType;
  /** The place's state and county: the steps come from its health authority (setup.ts setupForPlace). Absent = Utah. */
  state?: UsState;
  county?: string | null;
  stage: number;
  /** Include first-hire steps (default true; they only appear at stages 2–5, per setup.ts). */
  hiring?: boolean;
  /** Hand-ticked steps (PATCH /opening/:stepId). Ignored for auto steps. */
  done: Record<string, boolean>;
  location: { permitNumber?: string; permitExpiresOn?: IsoDate };
  /** Active people at this location. */
  employees: Pick<Employee, "id" | "name" | "startedOn" | "active">[];
  /** Employee ids of the owners (for "Your own food handler permit"). */
  ownerEmployeeIds?: Id[];
  credentials: Pick<Credential, "employeeId" | "kind" | "expiresOn" | "verifiedAt">[];
  /** At least one active checklist/log task at this location. */
  logsSetUp: boolean;
  today: IsoDate;
  /**
   * When food work starts here (journey.ts foodWorkStart). null = planning mode: nobody's food-handler clock has started
   * (review A11: a planner was told "permit was due 1 day ago" on day 31 of planning). Absent = each person's startedOn.
   */
  foodWorkStart?: IsoDate | null;
}

/** Roadmap steps that belong to the business side, not food safety (research Part A2–A4, A7, Part B1–B3). */
export const ASK_A_PRO_STEP_IDS = new Set(["state-registration", "ein", "city-business-license", "sales-tax-license", "restaurant-tax", "temp-sales-tax", "workers-comp", "ui-account", "withholding-account", "i9-w4", "new-hire-report"]);
/** The permit itself: done only when a permit number is on the location (and not expired). */
export const PERMIT_STEP_IDS = new Set(["food-permit", "mehk-permit", "mfb-permit", "temp-event-permit"]);
/** Conditional steps (only if something happens later): shown, never blocking. */
const CONDITIONAL_STEP_IDS = new Set(["cfsm-replace"]);
const AUTO_STEP_IDS = new Set([...PERMIT_STEP_IDS, "cfsm", "food-handler-self", "food-handler-staff", "logs"]);

export const isAutoOpeningStep = (id: string) => AUTO_STEP_IDS.has(id);

const LOGS_STEP = {
  id: "logs", title: "Daily logs and checklists set up", why: "The daily checks are how you show an inspector the food was kept safe.",
  who: "You", link: "", citation: "CleanCleaver setup (tasks and logs for this location)", verifyLocal: false,
};

const validCred = (c: Pick<Credential, "expiresOn">, today: IsoDate) => !c.expiresOn || c.expiresOn >= today;

export function openingGate(i: OpeningGateInput): OpeningGate {
  const setup = setupForPlace(i.stage, i.permitType, { hiring: i.hiring ?? true }, { state: i.state ?? "UT", county: i.county });
  const people = i.employees.filter((e) => e.active);
  const owners = new Set(i.ownerEmployeeIds ?? []);

  /** Food handler status for a group of people (onboarding.ts windows: course by day 14, permit by day 30). */
  const handlers = (group: typeof people) => {
    const late: string[] = [], due: string[] = [];
    for (const e of group) {
      const st = onboardingStatus({ employee: { startedOn: e.startedOn }, lessons: [], signatures: [], credentials: i.credentials.filter((c) => c.employeeId === e.id), managerApproved: false, today: i.today, ...(i.foodWorkStart !== undefined ? { workStartsOn: i.foodWorkStart } : {}), ...(i.state ? { state: i.state, county: i.county ?? null } : {}) });
      // No card rule here (Nevada outside Clark County), or the card is on file.
      if (st.steps.find((s) => s.step === "food_handler_course")?.done ?? true) continue;
      // SNHD: before the first shift (2-601(A)). A flag, never "late" days.
      if (st.cardRule.beforeFirstWork) { due.push(`${e.name}: card needed before first shift`); continue; }
      if (!st.clockRunning) { due.push(`${e.name}: course within 14 days and permit within 30 days of the first day serving food`); continue; }
      if (st.permitDueInDays < 0) late.push(`${e.name}: permit was due ${-st.permitDueInDays} day${st.permitDueInDays === -1 ? "" : "s"} ago`);
      else due.push(`${e.name}: course due in ${Math.max(0, st.courseDueInDays)} days, permit in ${st.permitDueInDays}`);
    }
    return { done: late.length === 0 && due.length === 0, onTrack: late.length === 0, detail: [...late, ...due].join("; ") || undefined };
  };

  const steps: OpeningStep[] = setup.map((s: SetupStep) => {
    const kind: OpeningStepKind = ASK_A_PRO_STEP_IDS.has(s.id) ? "ask_a_pro" : "food_safety";
    const base: OpeningStep = {
      id: s.id, title: s.title, why: s.why, kind, done: i.done[s.id] === true, onTrack: false, auto: false,
      required: kind === "food_safety" && !CONDITIONAL_STEP_IDS.has(s.id),
      who: s.issuer, link: s.link, citation: s.citation, verifyLocal: s.verifyLocal, ...(s.deadline ? { deadline: s.deadline } : {}),
      ...(s.phone ? { phone: s.phone } : {}), ...(s.bring?.length ? { bring: s.bring } : {}),
    };
    if (PERMIT_STEP_IDS.has(s.id)) {
      const on = !!i.location.permitNumber;
      const expired = on && !!i.location.permitExpiresOn && i.location.permitExpiresOn < i.today;
      return { ...base, auto: true, done: on && !expired, detail: expired ? `The permit on file expired on ${i.location.permitExpiresOn}.` : on ? undefined : "Add the permit number to the location when you have it." };
    }
    if (s.id === "cfsm") {
      const has = i.credentials.some((c) => c.kind === "certified_food_safety_manager" && validCred(c, i.today) && people.some((e) => e.id === c.employeeId));
      return { ...base, auto: true, done: has, detail: has ? undefined : "Upload a certified food safety manager certificate for someone on the team." };
    }
    if (s.id === "food-handler-self") {
      const h = handlers(people.filter((e) => owners.size === 0 || owners.has(e.id)));
      return { ...base, auto: true, ...h };
    }
    if (s.id === "food-handler-staff") {
      const h = handlers(people.filter((e) => !owners.has(e.id)));
      return { ...base, auto: true, ...h };
    }
    return base;
  });
  // "Get ready for your opening inspection" (preopening.ts): optional, never blocking; "Ready for the inspector" is the tick.
  if (i.permitType === "food_establishment" || i.permitType === "mobile_food_business") {
    const place = { state: i.state ?? "UT", county: i.county };
    const items = preOpeningChecklist(rulesWorld(), place);
    const a = authorityFor(stateOrDefault(place.state), place.county);
    const plan = inspectionPlanLine(rulesWorld(), place);
    steps.push({
      id: PRE_OPENING_STEP_ID, title: "Get ready for your opening inspection", why: "What the inspector looks at before the permit. Each item opens the place in the app that proves it.",
      kind: "food_safety", done: i.done[PRE_OPENING_STEP_ID] === true, onTrack: false, auto: false, required: false,
      who: a.kind === "confirmed" ? a.department.name : "Your health department", link: "", citation: [...new Set(items.map((x) => x.from))].join("; "), verifyLocal: true,
      checklist: items.map((x) => ({ ...x, stepId: preOpeningItemStepId(x.id), done: i.done[preOpeningItemStepId(x.id)] === true })),
      ...(plan ? { plan: plan.line } : {}),
    });
  }
  steps.push({ ...LOGS_STEP, kind: "food_safety", done: i.logsSetUp, onTrack: false, auto: true, required: true, ...(i.logsSetUp ? {} : { detail: "Set up the kitchen: hours, areas and menu create the daily checks." }) });

  const blocking = steps.filter((s) => s.required && !s.done && !s.onTrack).map((s) => ({ id: s.id, title: s.title, ...(s.detail ? { detail: s.detail } : {}) }));
  return { ready: blocking.length === 0, steps, blocking, notLegalAdvice: NOT_LEGAL_ADVICE };
}

/** The three-way answer the Open legally screens show (journey.ts legacyPhase reads it from Location.openingPhase). */
export const OPENING_PHASE_IDS = ["planning", "opening_soon", "open"] as const;
export type OpeningPhaseId = (typeof OPENING_PHASE_IDS)[number];

/** Every step id the gate can show for some permit type (PATCH validation, docs). */
/** Every step id the gate can show (PATCH validation): the setup steps, Nevada's added ones, and the pre-opening checklist's items. */
class OpeningStepIds extends Set<string> {
  override has(id: string): boolean { return super.has(id) || isPreOpeningItemStepId(id); }
}
export const OPENING_STEP_IDS: Set<string> = new OpeningStepIds([...BUSINESS_SETUP.map((s) => s.id), LOGS_STEP.id, "grade-card", "confirm-authority", "change-of-ownership", "reinspection-request", PRE_OPENING_STEP_ID]);
/** Is this a step the gate shows, or an item inside one (a pre-opening checklist item)? */
export const gateHasStep = (g: { steps: readonly { id: string; checklist?: readonly { stepId: string }[] }[] }, id: string) => g.steps.some((s) => s.id === id || !!s.checklist?.some((c) => c.stepId === id));

/**
 * The "Can we open yet?" headline, told the same way the owner told setup (audit 2026-09-29 #14a). A place that said
 * "Already open" is serving food: its permit steps aren't misses in red ("Not ready to open"), they're papers to confirm
 * ("Do you have these on file?"). Planning or opening soon, or no answer: the gate as it is.
 */
export function openingHeadline(gate: Pick<OpeningGate, "ready" | "blocking">, phase?: "planning" | "opening_soon" | "open"): { tone: "go" | "stop" | "info"; title: string; lead: string } {
  if (gate.ready) return { tone: "go", title: "Ready to open", lead: "Your permit, a certified manager, your team's food handler permits and your daily logs are all set." };
  const n = gate.blocking.length;
  if (phase === "open") return { tone: "info", title: "Do you have these on file?", lead: n === 1 ? "You said you're already open. Tick it when you have it:" : `You said you're already open. Tick each one you have (${n}):` };
  return { tone: "stop", title: "Not ready to open", lead: n === 1 ? "One thing first:" : `${n} things first:` };
}
